/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const ENTRY_BY_MINT_DISCRIMINATOR = new Uint8Array([
  188, 191, 157, 194, 68, 82, 181, 126,
]);

export function getEntryByMintDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ENTRY_BY_MINT_DISCRIMINATOR,
  );
}

export type EntryByMint = {
  discriminator: ReadonlyUint8Array;
  /** The market this entry belongs to */
  market: Address;
  /** The base mint */
  baseMint: Address;
  /** The entry_id for reverse lookup */
  entryId: ReadonlyUint8Array;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

export type EntryByMintArgs = {
  /** The market this entry belongs to */
  market: Address;
  /** The base mint */
  baseMint: Address;
  /** The entry_id for reverse lookup */
  entryId: ReadonlyUint8Array;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link EntryByMintArgs} account data. */
export function getEntryByMintEncoder(): FixedSizeEncoder<EntryByMintArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['market', getAddressEncoder()],
      ['baseMint', getAddressEncoder()],
      ['entryId', fixEncoderSize(getBytesEncoder(), 32)],
      ['bump', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 7)],
    ]),
    (value) => ({ ...value, discriminator: ENTRY_BY_MINT_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link EntryByMint} account data. */
export function getEntryByMintDecoder(): FixedSizeDecoder<EntryByMint> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['market', getAddressDecoder()],
    ['baseMint', getAddressDecoder()],
    ['entryId', fixDecoderSize(getBytesDecoder(), 32)],
    ['bump', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 7)],
  ]);
}

/** Gets the codec for {@link EntryByMint} account data. */
export function getEntryByMintCodec(): FixedSizeCodec<
  EntryByMintArgs,
  EntryByMint
> {
  return combineCodec(getEntryByMintEncoder(), getEntryByMintDecoder());
}

export function decodeEntryByMint<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<EntryByMint, TAddress>;
export function decodeEntryByMint<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<EntryByMint, TAddress>;
export function decodeEntryByMint<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<EntryByMint, TAddress> | MaybeAccount<EntryByMint, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getEntryByMintDecoder(),
  );
}

export async function fetchEntryByMint<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<EntryByMint, TAddress>> {
  const maybeAccount = await fetchMaybeEntryByMint(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeEntryByMint<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<EntryByMint, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeEntryByMint(maybeAccount);
}

export async function fetchAllEntryByMint(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<EntryByMint>[]> {
  const maybeAccounts = await fetchAllMaybeEntryByMint(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeEntryByMint(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<EntryByMint>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeEntryByMint(maybeAccount));
}

export function getEntryByMintSize(): number {
  return 112;
}
